import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { Icon } from '@iconify/react';
import JoditEditor from "jodit-react";
import { useDispatch, useSelector } from "react-redux";
import { getInvoiceSettings, updateInvoiceSettings } from "../../../../../redux/actions/SocietyPortal/InvoiceSettingAction/InvoiceSettingAction";
import { getInvoiceTypes } from "../../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction";
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import { notify } from "../../../../../utils/notification";


// Receipt payment modes (receipts.payment_mode). payment_mode setting is stored
// as a comma-separated list of these; the backend enforces it on receipts.
const PAYMENT_MODE_OPTIONS = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CHEQUE', label: 'Cheque' },
    { value: 'NEFT', label: 'NEFT' },
    { value: 'RTGS', label: 'RTGS' },
    { value: 'UPI', label: 'UPI' },
    { value: 'CARD', label: 'Card' },
    { value: 'DD', label: 'DD' },
    { value: 'OTHER', label: 'Other' }
];
// Older free-text values ("Cash, Cheque, Online") map onto the same codes.
const PAYMENT_MODE_ALIASES = {
    ONLINE: ['NEFT', 'RTGS', 'UPI', 'CARD'],
    'BANK TRANSFER': ['NEFT', 'RTGS'],
    'NET BANKING': ['NEFT', 'RTGS'],
    IMPS: ['NEFT'],
    'DEMAND DRAFT': ['DD'],
    'CREDIT CARD': ['CARD'],
    'DEBIT CARD': ['CARD']
};
const parsePaymentModes = (value) => {
    const codes = PAYMENT_MODE_OPTIONS.map((o) => o.value);
    const out = new Set();
    String(value || '').split(/[,;/|]/).map((t) => t.replace(/\s+/g, ' ').trim().toUpperCase()).filter(Boolean)
        .forEach((t) => {
            if (codes.includes(t)) out.add(t);
            (PAYMENT_MODE_ALIASES[t] || []).forEach((m) => out.add(m));
        });
    return [...out];
};

const ALL_COMPONENTS = ['INTEREST', 'ARREAR', 'DEBIT_NOTE_ARREAR', 'PRINCIPAL'];
const DEFAULT_ORDER = ['INTEREST', 'ARREAR', 'DEBIT_NOTE_ARREAR', 'PRINCIPAL'];
const COMPONENT_LABELS = {
    PRINCIPAL: 'Principal Amount',
    INTEREST: 'Interest',
    ARREAR: 'Arrears',
    DEBIT_NOTE_ARREAR: 'Debit Note Arrears',
};
const COMPONENT_ICONS = {
    PRINCIPAL: 'mdi:cash',
    INTEREST: 'mdi:percent-circle-outline',
    ARREAR: 'mdi:clock-alert-outline',
    DEBIT_NOTE_ARREAR: 'mdi:note-alert-outline',
};

// Pristine defaults. Kept at module scope so switching bill type can reset to
// them: the settings GET returns { exists: false } for a type that has no row
// yet, and the mapper's `?? prev` fallback then left every field showing the
// previously selected bill type's values.
const DEFAULT_SETTINGS = {
    // Invoice Header & Labels
    invoice_header: "",
    area_label: "Area",
    header_font_size: "24",
    print_font_size: null,
    // Print template / bill format ('modern' | 'classic' | 'gst')
    invoice_template: "modern",

    // Interest Settings
    interest_charge_type: "Daily",
    roi: null,
    grace_period: 0,
    interest_from_grace_period: 0,
    apply_interest_on_interest_arrears: 0,
    apply_interest_on_outstanding_amt: 0,
    show_interest_before_due_date: 0,
    show_interest_after_due_date: 0,
    show_interest_note: 0,
    hide_zero_interest_note: 0,
    interest_notes: null,

    // Arrear Settings
    arrear_display: 1,
    hide_principal_arrear: 0,
    hide_interest_arrear: 0,

    // Display Settings
    round_off: 0,
    show_invoice_note: 1,
    show_receipt_note: 1,
    show_debit_note_note: 1,
    show_credit_note_note: 1,
    invoice_notes: null,
    receipt_notes: null,
    debit_note_notes: null,
    credit_note_notes: null,
    show_pan_card_no: 0,
    show_phone: 0,
    show_email: 0,
    show_tenant: 0,
    show_flat_area: 1,
    show_flat_address: 0,
    show_parking_no: 0,
    show_wing_title: 0,
    show_van: 0,
    show_gst_no: 0,
    show_counter_foil: 0,
    show_detailed_gst: 0,
    show_excess_paid: 0,
    show_receipt_in_invoice: 0,
    show_total_principal: 0,
    show_total_interest: 0,
    hide_tariff_head: 0,

    // Billing Settings
    amount_editable: 1,
    exceed_due_date: 0,
    restrict_no_of_days: "0",
    billed_to_shipped: 0,
    proforma: null,
    unit_format: null,

    // Payment Settings
    payment_mode: null,
    setoff_order: null,

    // Notification Settings
    send_invoice_email: 0,
    send_invoice_sms: 0,
    send_invoice_receipt_email: 0,
    send_invoice_receipt_sms: 0,
};

const FunctionalSetting = () => {
    const dispatch = useDispatch();

    // Get society_id from localStorage
    const societyId = selectedSocietyStorer.getSocietyId();

    // State for selected invoice type
    const [invoiceTypeId, setInvoiceTypeId] = useState("");

    const { invoiceSettings, loading, updateLoading } = useSelector(state => state.InvoiceSettingReducer);
    const [savedAt, setSavedAt] = useState(null);
    const { invoiceTypesData, loading: invoiceTypesLoading } = useSelector(state => state.InvoiceTypeReducers);


    // Settings state matching API response structure
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);

    // Fetch invoice types on mount
    useEffect(() => {
        if (societyId) {
            dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        }
    }, [dispatch, societyId]);

    // Fetch invoice settings when invoice type is selected
    useEffect(() => {
        if (societyId && invoiceTypeId) {
            dispatch(getInvoiceSettings({ society_id: societyId, invoice_type_id: invoiceTypeId }));
        }
    }, [dispatch, societyId, invoiceTypeId]);

    // Map API response to state when invoice settings are fetched
    useEffect(() => {
        if (invoiceSettings) {
            // A bill type with no saved row answers { exists: false }. Falling
            // back to `prev` there carried the previous type's settings across;
            // fall back to the defaults instead so each bill type starts clean.
            const prev = DEFAULT_SETTINGS;
            setSettings(() => ({
                ...prev,
                // Invoice Header & Labels
                invoice_header: invoiceSettings.invoice_header ?? prev.invoice_header,
                area_label: invoiceSettings.area_label ?? prev.area_label,
                header_font_size: invoiceSettings.header_font_size ?? prev.header_font_size,
                print_font_size: invoiceSettings.print_font_size ?? prev.print_font_size,
                invoice_template: invoiceSettings.invoice_template ?? prev.invoice_template,

                // Interest Settings
                interest_charge_type: invoiceSettings.interest_charge_type ?? prev.interest_charge_type,
                roi: invoiceSettings.roi ?? prev.roi,
                grace_period: invoiceSettings.grace_period ?? prev.grace_period,
                interest_from_grace_period: invoiceSettings.interest_from_grace_period ?? prev.interest_from_grace_period,
                apply_interest_on_interest_arrears: invoiceSettings.apply_interest_on_interest_arrears ?? prev.apply_interest_on_interest_arrears,
                apply_interest_on_outstanding_amt: invoiceSettings.apply_interest_on_outstanding_amt ?? prev.apply_interest_on_outstanding_amt,
                show_interest_before_due_date: invoiceSettings.show_interest_before_due_date ?? prev.show_interest_before_due_date,
                show_interest_after_due_date: invoiceSettings.show_interest_after_due_date ?? prev.show_interest_after_due_date,
                show_interest_note: invoiceSettings.show_interest_note ?? prev.show_interest_note,
                hide_zero_interest_note: invoiceSettings.hide_zero_interest_note ?? prev.hide_zero_interest_note,
                interest_notes: invoiceSettings.interest_notes ?? prev.interest_notes,

                // Arrear Settings
                arrear_display: invoiceSettings.arrear_display ?? prev.arrear_display,
                hide_principal_arrear: invoiceSettings.hide_principal_arrear ?? prev.hide_principal_arrear,
                hide_interest_arrear: invoiceSettings.hide_interest_arrear ?? prev.hide_interest_arrear,

                // Display Settings
                round_off: invoiceSettings.round_off ?? prev.round_off,
                show_invoice_note: invoiceSettings.show_invoice_note ?? prev.show_invoice_note,
                show_receipt_note: invoiceSettings.show_receipt_note ?? prev.show_receipt_note,
                show_debit_note_note: invoiceSettings.show_debit_note_note ?? prev.show_debit_note_note,
                show_credit_note_note: invoiceSettings.show_credit_note_note ?? prev.show_credit_note_note,
                invoice_notes: invoiceSettings.invoice_notes ?? prev.invoice_notes,
                receipt_notes: invoiceSettings.receipt_notes ?? prev.receipt_notes,
                debit_note_notes: invoiceSettings.debit_note_notes ?? prev.debit_note_notes,
                credit_note_notes: invoiceSettings.credit_note_notes ?? prev.credit_note_notes,
                show_pan_card_no: invoiceSettings.show_pan_card_no ?? prev.show_pan_card_no,
                show_phone: invoiceSettings.show_phone ?? prev.show_phone,
                show_email: invoiceSettings.show_email ?? prev.show_email,
                show_tenant: invoiceSettings.show_tenant ?? prev.show_tenant,
                show_flat_area: invoiceSettings.show_flat_area ?? prev.show_flat_area,
                show_flat_address: invoiceSettings.show_flat_address ?? prev.show_flat_address,
                show_parking_no: invoiceSettings.show_parking_no ?? prev.show_parking_no,
                show_wing_title: invoiceSettings.show_wing_title ?? prev.show_wing_title,
                show_van: invoiceSettings.show_van ?? prev.show_van,
                show_gst_no: invoiceSettings.show_gst_no ?? prev.show_gst_no,
                show_counter_foil: invoiceSettings.show_counter_foil ?? prev.show_counter_foil,
                show_detailed_gst: invoiceSettings.show_detailed_gst ?? prev.show_detailed_gst,
                show_excess_paid: invoiceSettings.show_excess_paid ?? prev.show_excess_paid,
                show_receipt_in_invoice: invoiceSettings.show_receipt_in_invoice ?? prev.show_receipt_in_invoice,
                show_total_principal: invoiceSettings.show_total_principal ?? prev.show_total_principal,
                show_total_interest: invoiceSettings.show_total_interest ?? prev.show_total_interest,
                hide_tariff_head: invoiceSettings.hide_tariff_head ?? prev.hide_tariff_head,

                // Billing Settings
                amount_editable: invoiceSettings.amount_editable ?? prev.amount_editable,
                exceed_due_date: invoiceSettings.exceed_due_date ?? prev.exceed_due_date,
                restrict_no_of_days: invoiceSettings.restrict_no_of_days ?? prev.restrict_no_of_days,
                billed_to_shipped: invoiceSettings.billed_to_shipped ?? prev.billed_to_shipped,
                proforma: invoiceSettings.proforma ?? prev.proforma,
                unit_format: invoiceSettings.unit_format ?? prev.unit_format,

                // Payment Settings
                payment_mode: invoiceSettings.payment_mode ?? prev.payment_mode,
                setoff_order: invoiceSettings.setoff_order ?? prev.setoff_order,

                // Notification Settings
                send_invoice_email: invoiceSettings.send_invoice_email ?? prev.send_invoice_email,
                send_invoice_sms: invoiceSettings.send_invoice_sms ?? prev.send_invoice_sms,
                send_invoice_receipt_email: invoiceSettings.send_invoice_receipt_email ?? prev.send_invoice_receipt_email,
                send_invoice_receipt_sms: invoiceSettings.send_invoice_receipt_sms ?? prev.send_invoice_receipt_sms,
            }));
        }
    }, [invoiceSettings]);

    const [activeTab, setActiveTab] = useState("billing");
    const paymentNoteEditorRef = useRef(null);
    const invoiceNoteEditorRef = useRef(null);
    const debitNoteEditorRef = useRef(null);
    const creditNoteEditorRef = useRef(null);

    const editorConfig = useMemo(() => ({
        readonly: false,
        placeholder: "Enter note here...",
        height: 250,
        toolbarButtonSize: "small",
        buttons: [
            'bold', 'italic', 'underline', 'strikethrough', '|',
            'ul', 'ol', '|',
            'font', 'fontsize', 'brush', '|',
            'align', '|',
            'link', '|',
            'undo', 'redo', '|',
            'eraser', 'source'
        ],
        removeButtons: ['about'],
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
    }), []);

    // Toggle boolean fields (0/1 values)
    const handleToggle = (field) => {
        setSettings(prev => ({
            ...prev,
            [field]: prev[field] === 1 ? 0 : 1
        }));
    };

    // Handle field value changes
    const handleChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Setoff order drag-and-drop
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const setoffOrder = useMemo(() => {
        if (Array.isArray(settings.setoff_order) && settings.setoff_order.length === ALL_COMPONENTS.length) {
            return settings.setoff_order;
        }
        return [...DEFAULT_ORDER];
    }, [settings.setoff_order]);

    const handleDragStart = useCallback((index) => {
        setDragIndex(index);
    }, []);

    const handleDragOver = useCallback((e, index) => {
        e.preventDefault();
        setDragOverIndex(index);
    }, []);

    const handleDrop = useCallback((index) => {
        if (dragIndex === null || dragIndex === index) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }
        const newOrder = [...setoffOrder];
        const [removed] = newOrder.splice(dragIndex, 1);
        newOrder.splice(index, 0, removed);
        handleChange('setoff_order', newOrder);
        setDragIndex(null);
        setDragOverIndex(null);
    }, [dragIndex, setoffOrder]);

    const handleDragEnd = useCallback(() => {
        setDragIndex(null);
        setDragOverIndex(null);
    }, []);

    const handleResetOrder = () => {
        handleChange('setoff_order', [...DEFAULT_ORDER]);
    };

    const handleSave = () => {
        if (!invoiceTypeId) {
            notify('error', 'Select a bill type first.');
            return;
        }
        const payload = {
            ...settings,
            society_id: societyId,
            invoice_type_id: invoiceTypeId,
            // Save said nothing either way, so a rejected save looked exactly
            // like a saved one. The settings are read back from the API so what
            // is on screen is what was stored.
            onSuccess: () => {
                setSavedAt(new Date());
                dispatch(getInvoiceSettings({ society_id: societyId, invoice_type_id: invoiceTypeId }));
            }
        };
        dispatch(updateInvoiceSettings(payload));
    };

    const tabs = [
        { id: 'billing', label: 'Billing Settings', icon: 'mdi:receipt-text-outline' },
        { id: 'payment', label: 'Payment Settings', icon: 'mdi:credit-card-outline' },
        { id: 'notification', label: 'Notifications', icon: 'mdi:bell-outline' },
        { id: 'general', label: 'General', icon: 'mdi:cog-outline' },
        { id: 'notes', label: 'Bill Notes', icon: 'mdi:file-document-edit-outline' },
    ];

    return (
        <div className="invoice-setup-page">
            {/* Page Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <div>
                    <h6 className="fw-semibold mb-0 text-xl">Functional Settings</h6>
                    <p className="text-secondary-light text-sm mb-0 mt-1">
                        Configure system functional settings for billing, payments, and notifications
                    </p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button
                        onClick={handleSave}
                        className="btn d-flex align-items-center gap-2 px-20 py-10 radius-8 text-white"
                        style={{ backgroundColor: '#f59e0b' }}
                        disabled={updateLoading || !invoiceTypeId}
                    >
                        {updateLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Icon icon="mdi:content-save" className="text-lg" />
                                Save Changes
                            </>
                        )}
                    </button>
                    {savedAt && !updateLoading && (
                        <span className="d-flex align-items-center gap-1 text-sm text-success-main ms-2">
                            <Icon icon="mdi:check-circle-outline" />
                            Saved at {savedAt.toLocaleTimeString()}
                        </span>
                    )}
                </div>
            </div>

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-24 bg-primary-50">
                <div className="card-body p-16 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:information-outline" className="text-primary-600 text-xl" />
                    </div>
                    <p className="text-sm text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> These settings affect how billing, payments, and notifications work across the entire society.
                        Changes are applied after saving.
                    </p>
                </div>
            </div>

            {/* Invoice Type Selection */}
            <div className="card radius-12 border-0 shadow-sm mb-24 billing-type-card">
                <div className="card-body p-24">
                    <div className="row align-items-center">
                        <div className="col-lg-4 col-md-5 mb-3 mb-md-0">
                            <div className="d-flex align-items-center gap-3">
                                <div className="icon-wrapper">
                                    <Icon icon="mdi:receipt-text-outline" className="text-2xl" />
                                </div>
                                <div>
                                    <label className="form-label fw-semibold mb-0">Select Billing Type</label>
                                    <p className="text-secondary-light text-sm mb-0">Choose a billing type to configure</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-8 col-md-7">
                            <div className="dropdown-wrapper">
                                <select
                                    className="form-select custom-dropdown"
                                    value={invoiceTypeId}
                                    onChange={(e) => setInvoiceTypeId(e.target.value)}
                                    disabled={invoiceTypesLoading}
                                >
                                    <option value="">-- Select Billing Type --</option>
                                    {invoiceTypesData?.map((type) => (
                                        <option key={type.invoice_type_id} value={type.invoice_type_id}>
                                            {type.invoice_title} ({type.invoice_type})
                                        </option>
                                    ))}
                                </select>
                                {invoiceTypesLoading && (
                                    <div className="dropdown-loader">
                                        <span className="spinner-border spinner-border-sm text-primary"></span>
                                    </div>
                                )}
                            </div>
                            {!invoiceTypeId && (
                                <div className="alert alert-warning d-flex align-items-center gap-2 mt-12 mb-0 py-8 px-12 radius-8">
                                    <Icon icon="mdi:alert-circle-outline" className="text-lg" />
                                    <small>Please select a billing type to configure its settings</small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Settings Card with Tabs */}
            {!invoiceTypeId ? (
                <div className="card radius-12 border-0 shadow-sm">
                    <div className="card-body p-40 text-center">
                        <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                            <Icon icon="mdi:cog-outline" className="text-secondary-light text-4xl" />
                        </div>
                        <h6 className="fw-semibold mb-2">No Billing Type Selected</h6>
                        <p className="text-secondary-light text-sm mb-0">
                            Please select a billing type from the dropdown above to configure its functional settings.
                        </p>
                    </div>
                </div>
            ) : loading ? (
                <div className="card radius-12 border-0 shadow-sm">
                    <div className="card-body p-40 text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h6 className="fw-semibold mb-2">Loading Settings...</h6>
                        <p className="text-secondary-light text-sm mb-0">
                            Please wait while we fetch the settings for this billing type.
                        </p>
                    </div>
                </div>
            ) : (
            <div className="card radius-12 border-0 shadow-sm">
                {/* Tab Navigation */}
                <div className="card-header py-0 px-0 bg-white border-bottom">
                    <div className="d-flex flex-wrap overflow-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`tab-btn d-flex align-items-center gap-2 px-24 py-16 border-0 bg-transparent text-nowrap ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <Icon icon={tab.icon} className="text-xl" />
                                <span className="fw-medium">{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="card-body p-24">
                    {/* Billing Settings */}
                    {activeTab === 'billing' && (
                        <div className="row g-4">
                            {/* Invoice Header Card */}
                            <div className="col-12">
                                <div className="card radius-12 border-0 shadow-sm overflow-hidden">
                                    <div className="card-header py-16 px-20 bg-gradient-primary border-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="w-40-px h-40-px bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center">
                                                <Icon icon="mdi:file-document-outline" className="text-2xl text-white" />
                                            </div>
                                            <h6 className="fw-semibold mb-0 text-white">Invoice Header Configuration</h6>
                                        </div>
                                    </div>
                                    <div className="card-body p-20">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold mb-2">Invoice Header Title</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-lg radius-8"
                                                    value={settings.invoice_header || ""}
                                                    onChange={(e) => handleChange('invoice_header', e.target.value)}
                                                    placeholder="e.g., Maintenance Bill"
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold mb-2">Area Label</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-lg radius-8"
                                                    value={settings.area_label || ""}
                                                    onChange={(e) => handleChange('area_label', e.target.value)}
                                                    placeholder="e.g., Area"
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold mb-2">Header Font Size</label>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-lg radius-8"
                                                    value={settings.header_font_size || ""}
                                                    onChange={(e) => handleChange('header_font_size', e.target.value)}
                                                />
                                            </div>
                                            {/* Bill / Invoice print format. Keep keys in sync with
                                                InvoiceTemplateRenderer.jsx (modern | classic | gst). */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold mb-2">Bill / Invoice Format</label>
                                                <select
                                                    className="form-select form-select-lg radius-8"
                                                    value={settings.invoice_template || "modern"}
                                                    onChange={(e) => handleChange('invoice_template', e.target.value)}
                                                >
                                                    <option value="modern">Modern (default)</option>
                                                    <option value="classic">Classic Bill + Receipt</option>
                                                    <option value="gst">GST Tax Invoice</option>
                                                </select>
                                                <small className="text-secondary-light">Choose the layout used when printing this billing type's invoices.</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Interest Configuration Card */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border-0 shadow-sm h-100">
                                    <div className="card-header py-14 px-20 bg-danger-50 border-0">
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="mdi:percent" className="text-2xl text-danger-600" />
                                            <h6 className="fw-semibold mb-0 text-danger-600">Interest Configuration</h6>
                                        </div>
                                    </div>
                                    <div className="card-body p-20">
                                        <div className="mb-16">
                                            <label className="form-label fw-semibold mb-2">Interest Charge Type</label>
                                            {/* Only Daily / Monthly are wired
                                                on the backend (invoiceSettingValidator.interest_charge_type
                                                whitelists these two). Yearly / Simple / Compound existed
                                                on the UI but the API would 400 on save. */}
                                            <select
                                                className="form-select form-select-lg radius-8"
                                                value={settings.interest_charge_type || ""}
                                                onChange={(e) => handleChange('interest_charge_type', e.target.value)}
                                            >
                                                <option value="Daily">Daily</option>
                                                <option value="Monthly">Monthly</option>
                                            </select>
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-6">
                                                <label className="form-label fw-semibold mb-2">Rate of Interest (%)</label>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-lg radius-8"
                                                    value={settings.roi || ""}
                                                    onChange={(e) => handleChange('roi', e.target.value)}
                                                    placeholder="18"
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label fw-semibold mb-2">Grace Period (Days)</label>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-lg radius-8"
                                                    value={settings.grace_period || 0}
                                                    onChange={(e) => handleChange('grace_period', parseInt(e.target.value) || 0)}
                                                />
                                            </div>
                                        </div>
                                        {/* The Start/End day inputs that used to sit here were never read by
                                            any calculation. The window below is the rule the interest
                                            calculation and the Interest Report actually use. */}
                                        <div className="mt-16 p-12 radius-8 bg-neutral-50 border">
                                            <div className="d-flex align-items-start gap-2">
                                                <Icon icon="mdi:information-outline" className="text-lg text-primary-600 mt-1" />
                                                <div>
                                                    <p className="text-sm fw-semibold mb-1">Interest is charged for</p>
                                                    <p className="text-xs text-secondary-light mb-0">
                                                        From the previous bill&apos;s due date{settings.interest_from_grace_period === 1 ? '' : ' plus the grace period'},
                                                        up to the current bill&apos;s period end.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Interest Options Card */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border-0 shadow-sm h-100">
                                    <div className="card-header py-14 px-20 bg-info-50 border-0">
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="mdi:cog-outline" className="text-2xl text-info-600" />
                                            <h6 className="fw-semibold mb-0 text-info-600">Interest Options</h6>
                                        </div>
                                    </div>
                                    <div className="card-body p-20">
                                        <div className="setting-item d-flex align-items-center justify-content-between p-16 radius-8 bg-neutral-50 mb-12">
                                            <span className="fw-medium">Interest from Grace Period</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input custom-switch"
                                                    type="checkbox"
                                                    checked={settings.interest_from_grace_period === 1}
                                                    onChange={() => handleToggle('interest_from_grace_period')}
                                                />
                                            </div>
                                        </div>
                                        <div className="setting-item d-flex align-items-center justify-content-between p-16 radius-8 bg-neutral-50 mb-12">
                                            <span className="fw-medium">Apply Interest on Interest Arrears</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input custom-switch"
                                                    type="checkbox"
                                                    checked={settings.apply_interest_on_interest_arrears === 1}
                                                    onChange={() => handleToggle('apply_interest_on_interest_arrears')}
                                                />
                                            </div>
                                        </div>
                                        <div className="setting-item d-flex align-items-center justify-content-between p-16 radius-8 bg-neutral-50">
                                            <span className="fw-medium">Apply Interest on Outstanding</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input custom-switch"
                                                    type="checkbox"
                                                    checked={settings.apply_interest_on_outstanding_amt === 1}
                                                    onChange={() => handleToggle('apply_interest_on_outstanding_amt')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Arrear Display Card */}
                            <div className="col-12">
                                <div className="card radius-12 border-0 shadow-sm">
                                    <div className="card-header py-14 px-20 bg-warning-50 border-0">
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="mdi:eye-off-outline" className="text-2xl text-warning-600" />
                                            <h6 className="fw-semibold mb-0 text-warning-600">Arrear Display Settings</h6>
                                        </div>
                                    </div>
                                    <div className="card-body p-20">
                                        <p className="text-secondary-light mb-16">Control visibility of arrear details on bills</p>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div
                                                    className={`setting-item d-flex align-items-center justify-content-between p-16 radius-8 cursor-pointer transition-all ${settings.hide_principal_arrear === 1 ? 'bg-danger-50 border border-danger-200' : 'bg-neutral-50 border border-transparent'}`}
                                                    onClick={() => handleToggle('hide_principal_arrear')}
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className={`w-40-px h-40-px rounded-circle d-flex align-items-center justify-content-center ${settings.hide_principal_arrear === 1 ? 'bg-danger-100' : 'bg-neutral-100'}`}>
                                                            <Icon icon={settings.hide_principal_arrear === 1 ? "mdi:eye-off" : "mdi:eye"} className={`text-xl ${settings.hide_principal_arrear === 1 ? "text-danger-600" : "text-secondary-light"}`} />
                                                        </div>
                                                        <span className="fw-medium">Hide Principal Arrear</span>
                                                    </div>
                                                    <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input custom-switch"
                                                            checked={settings.hide_principal_arrear === 1}
                                                            onChange={() => handleToggle('hide_principal_arrear')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div
                                                    className={`setting-item d-flex align-items-center justify-content-between p-16 radius-8 cursor-pointer transition-all ${settings.hide_interest_arrear === 1 ? 'bg-danger-50 border border-danger-200' : 'bg-neutral-50 border border-transparent'}`}
                                                    onClick={() => handleToggle('hide_interest_arrear')}
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className={`w-40-px h-40-px rounded-circle d-flex align-items-center justify-content-center ${settings.hide_interest_arrear === 1 ? 'bg-danger-100' : 'bg-neutral-100'}`}>
                                                            <Icon icon={settings.hide_interest_arrear === 1 ? "mdi:eye-off" : "mdi:eye"} className={`text-xl ${settings.hide_interest_arrear === 1 ? "text-danger-600" : "text-secondary-light"}`} />
                                                        </div>
                                                        <span className="fw-medium">Hide Interest Arrear</span>
                                                    </div>
                                                    <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input custom-switch"
                                                            checked={settings.hide_interest_arrear === 1}
                                                            onChange={() => handleToggle('hide_interest_arrear')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Payment Settings */}
                    {activeTab === 'payment' && (
                        <div className="row g-4">
                            {/* Billing Options Card */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border h-100">
                                    <div className="card-header py-12 px-16 bg-base border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:credit-card-check-outline" className="text-xl text-success-600" />
                                        <h6 className="fw-semibold mb-0">Billing Options</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Amount Editable</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.amount_editable === 1}
                                                    onChange={() => handleToggle('amount_editable')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Exceed Due Date</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.exceed_due_date === 1}
                                                    onChange={() => handleToggle('exceed_due_date')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                            <span className="text-sm fw-medium">Round Off Amount</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.round_off === 1}
                                                    onChange={() => handleToggle('round_off')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Mode & Restrictions Card */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border h-100">
                                    <div className="card-header py-12 px-16 bg-base border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:cash-multiple" className="text-xl text-info-600" />
                                        <h6 className="fw-semibold mb-0">Payment Restrictions</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <div className="mb-12">
                                            <label className="form-label text-sm fw-medium text-secondary-light mb-1">Allowed Payment Modes</label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {PAYMENT_MODE_OPTIONS.map((mode) => {
                                                    const selected = parsePaymentModes(settings.payment_mode);
                                                    const checked = selected.includes(mode.value);
                                                    return (
                                                        <label
                                                            key={mode.value}
                                                            className={`d-flex align-items-center gap-1 px-10 py-6 radius-6 border text-sm mb-0 ${checked ? 'border-primary bg-primary-50' : 'border-neutral-200'}`}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input mt-0"
                                                                checked={checked}
                                                                onChange={() => {
                                                                    const next = checked
                                                                        ? selected.filter((m) => m !== mode.value)
                                                                        : [...selected, mode.value];
                                                                    handleChange('payment_mode', PAYMENT_MODE_OPTIONS.map((o) => o.value).filter((v) => next.includes(v)).join(','));
                                                                }}
                                                            />
                                                            {mode.label}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                            <small className="text-secondary-light d-block mt-4">
                                                Receipts for this billing type's bills are accepted only in the ticked modes. Leave all unticked to allow every mode.
                                            </small>
                                        </div>
                                        <div className="mb-12">
                                            <label className="form-label text-sm fw-medium text-secondary-light mb-1">Restrict Back-dated Payments (days)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                className="form-control radius-8 h-44-px bg-neutral-50"
                                                value={settings.restrict_no_of_days || "0"}
                                                onChange={(e) => handleChange('restrict_no_of_days', e.target.value)}
                                            />
                                            <small className="text-secondary-light d-block mt-4">
                                                A payment cannot be dated more than this many days before today. 0 = no limit.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Setoff Order Card */}
                            <div className="col-12">
                                <div className="card radius-12 border border-primary">
                                    <div className="card-header py-12 px-16 bg-primary-50 border-bottom d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="mdi:sort" className="text-xl text-primary-600" />
                                            <h6 className="fw-semibold mb-0">Payment Setoff Order</h6>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                            onClick={handleResetOrder}
                                        >
                                            <Icon icon="mdi:restore" className="text-lg" />
                                            Reset to Default
                                        </button>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-secondary-light text-sm mb-16">
                                            When a payment is received, the amount is allocated to these components in the order below. Drag to reorder.
                                        </p>
                                        <div className="setoff-order-list">
                                            {setoffOrder.map((component, index) => (
                                                <div
                                                    key={component}
                                                    draggable
                                                    onDragStart={() => handleDragStart(index)}
                                                    onDragOver={(e) => handleDragOver(e, index)}
                                                    onDrop={() => handleDrop(index)}
                                                    onDragEnd={handleDragEnd}
                                                    className={`setoff-order-item d-flex align-items-center gap-3 p-12 mb-8 radius-8 border ${dragIndex === index ? 'border-primary bg-primary-50 opacity-50' : dragOverIndex === index ? 'border-primary-300 bg-primary-50' : 'border-neutral-200 bg-white'}`}
                                                    style={{ cursor: 'grab', transition: 'all 0.2s ease', userSelect: 'none' }}
                                                >
                                                    <div className="d-flex align-items-center gap-2 text-secondary-light">
                                                        <Icon icon="mdi:drag" className="text-xl" />
                                                        <span className="fw-bold text-sm" style={{ minWidth: '20px' }}>{index + 1}.</span>
                                                    </div>
                                                    <div className="w-36-px h-36-px rounded-circle bg-primary-50 d-flex align-items-center justify-content-center flex-shrink-0">
                                                        <Icon icon={COMPONENT_ICONS[component] || 'mdi:help-circle-outline'} className="text-lg text-primary-600" />
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <span className="fw-semibold text-sm">{COMPONENT_LABELS[component] || component}</span>
                                                        <span className="text-secondary-light text-xs d-block">{component}</span>
                                                    </div>
                                                    <Icon icon="mdi:drag-horizontal-variant" className="text-xl text-secondary-light" />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="d-flex align-items-center gap-2 mt-12 p-12 bg-info-50 radius-8">
                                            <Icon icon="mdi:information-outline" className="text-lg text-info-600 flex-shrink-0" />
                                            <small className="text-info-600">First component gets paid first when a receipt is created. The backend auto-calculates the breakdown.</small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Billing Options Card */}
                            <div className="col-12">
                                <div className="card radius-12 border border-primary">
                                    <div className="card-header py-12 px-16 bg-primary-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:file-cog-outline" className="text-xl text-primary-600" />
                                        <h6 className="fw-semibold mb-0">Additional Options</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <div className="row">
                                            <div className="col-lg-4 col-md-6 mb-12">
                                                <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                                    <span className="text-sm fw-medium">Billed to Shipped</span>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={settings.billed_to_shipped === 1}
                                                            onChange={() => handleToggle('billed_to_shipped')}
                                                                />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-4 col-md-6 mb-12">
                                                <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                                    <span className="text-sm fw-medium">Show Excess Paid</span>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={settings.show_excess_paid === 1}
                                                            onChange={() => handleToggle('show_excess_paid')}
                                                                />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-4 col-md-6 mb-12">
                                                <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                                    <span className="text-sm fw-medium">Show Receipt in Invoice</span>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={settings.show_receipt_in_invoice === 1}
                                                            onChange={() => handleToggle('show_receipt_in_invoice')}
                                                                />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Notification Settings */}
                    {activeTab === 'notification' && (
                        <div className="row g-4">
                            {/* Invoice Notifications */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border h-100">
                                    <div className="card-header py-12 px-16 bg-base border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:file-send-outline" className="text-xl text-primary-600" />
                                        <h6 className="fw-semibold mb-0">Invoice Notifications</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">Notify members when invoices are generated</p>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <div className="d-flex align-items-center gap-2">
                                                <Icon icon="mdi:email-outline" className="text-lg text-primary-600" />
                                                <span className="text-sm fw-medium">Send Invoice via Email</span>
                                            </div>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.send_invoice_email === 1}
                                                    onChange={() => handleToggle('send_invoice_email')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                            <div className="d-flex align-items-center gap-2">
                                                <Icon icon="mdi:message-text-outline" className="text-lg text-success-600" />
                                                <span className="text-sm fw-medium">Send Invoice via SMS</span>
                                            </div>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.send_invoice_sms === 1}
                                                    onChange={() => handleToggle('send_invoice_sms')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Receipt Notifications */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border h-100">
                                    <div className="card-header py-12 px-16 bg-base border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:receipt-outline" className="text-xl text-success-600" />
                                        <h6 className="fw-semibold mb-0">Receipt Notifications</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">Notify members when payments are received</p>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <div className="d-flex align-items-center gap-2">
                                                <Icon icon="mdi:email-check-outline" className="text-lg text-primary-600" />
                                                <span className="text-sm fw-medium">Send Receipt via Email</span>
                                            </div>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.send_invoice_receipt_email === 1}
                                                    onChange={() => handleToggle('send_invoice_receipt_email')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                            <div className="d-flex align-items-center gap-2">
                                                <Icon icon="mdi:message-check-outline" className="text-lg text-success-600" />
                                                <span className="text-sm fw-medium">Send Receipt via SMS</span>
                                            </div>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.send_invoice_receipt_sms === 1}
                                                    onChange={() => handleToggle('send_invoice_receipt_sms')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* General Settings */}
                    {activeTab === 'general' && (
                        <div className="row g-4">
                            {/* Display Options Card */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border h-100">
                                    <div className="card-header py-12 px-16 bg-base border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:file-eye-outline" className="text-xl text-primary-600" />
                                        <h6 className="fw-semibold mb-0">Display Options</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Show Flat Area</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_flat_area === 1}
                                                    onChange={() => handleToggle('show_flat_area')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Show Flat Address</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_flat_address === 1}
                                                    onChange={() => handleToggle('show_flat_address')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Show Wing Title</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_wing_title === 1}
                                                    onChange={() => handleToggle('show_wing_title')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                            <span className="text-sm fw-medium">Show Parking No.</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_parking_no === 1}
                                                    onChange={() => handleToggle('show_parking_no')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Display Options */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border h-100">
                                    <div className="card-header py-12 px-16 bg-base border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:receipt-text-outline" className="text-xl text-success-600" />
                                        <h6 className="fw-semibold mb-0">Invoice Display</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Show Counter Foil</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_counter_foil === 1}
                                                    onChange={() => handleToggle('show_counter_foil')}
                                                />
                                            </div>
                                        </div>
                                        {/* GST options live together here; "Show GST No." used to sit
                                            on the Bill Notes tab under Member Info Display. */}
                                        <div className="d-flex align-items-center gap-2 mt-12 mb-8">
                                            <Icon icon="mdi:percent-outline" className="text-md text-primary-600" />
                                            <span className="text-xs fw-semibold text-secondary-light text-uppercase">GST</span>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Show Detailed GST</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_detailed_gst === 1}
                                                    onChange={() => handleToggle('show_detailed_gst')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Show GST No.</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_gst_no === 1}
                                                    onChange={() => handleToggle('show_gst_no')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50 mb-8">
                                            <span className="text-sm fw-medium">Show Total Principal</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_total_principal === 1}
                                                    onChange={() => handleToggle('show_total_principal')}
                                                />
                                            </div>
                                        </div>
                                        <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                            <span className="text-sm fw-medium">Show Total Interest</span>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={settings.show_total_interest === 1}
                                                    onChange={() => handleToggle('show_total_interest')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Hide Options Card */}
                            <div className="col-12">
                                <div className="card radius-12 border border-warning">
                                    <div className="card-header py-12 px-16 bg-warning-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:eye-off-outline" className="text-xl text-warning-600" />
                                        <h6 className="fw-semibold mb-0">Hide Options</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <div className="row">
                                            <div className="col-lg-4 col-md-6 mb-12">
                                                <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                                    <span className="text-sm fw-medium">Hide Tariff Head</span>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={settings.hide_tariff_head === 1}
                                                            onChange={() => handleToggle('hide_tariff_head')}
                                                                />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-4 col-md-6 mb-12">
                                                <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                                    <span className="text-sm fw-medium">Hide Zero Interest Note</span>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={settings.hide_zero_interest_note === 1}
                                                            onChange={() => handleToggle('hide_zero_interest_note')}
                                                                />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-4 col-md-6 mb-12">
                                                <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                                    <span className="text-sm fw-medium">Show Tenant</span>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={settings.show_tenant === 1}
                                                            onChange={() => handleToggle('show_tenant')}
                                                                />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-4 col-md-6 mb-12">
                                                <div className="toggle-item d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50">
                                                    <span className="text-sm fw-medium">Show VAN</span>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={settings.show_van === 1}
                                                            onChange={() => handleToggle('show_van')}
                                                                />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bill Notes Settings */}
                    {activeTab === 'notes' && (
                        <div className="row g-4">
                            {/* Receipt Note */}
                            <div className="col-12">
                                <div className="card radius-12 border border-primary">
                                    <div className="card-header py-12 px-16 bg-primary-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:cash-check" className="text-xl text-primary-600" />
                                        <h6 className="fw-semibold mb-0">Receipt Note</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">This note will appear on payment receipts.</p>
                                        <JoditEditor
                                            ref={paymentNoteEditorRef}
                                            value={settings.receipt_notes || ""}
                                            config={editorConfig}
                                            onBlur={(newContent) => handleChange('receipt_notes', newContent)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Note */}
                            <div className="col-12">
                                <div className="card radius-12 border border-success">
                                    <div className="card-header py-12 px-16 bg-success-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:file-document-outline" className="text-xl text-success-600" />
                                        <h6 className="fw-semibold mb-0">Invoice Note</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">This note will appear on invoices sent to members.</p>
                                        <JoditEditor
                                            ref={invoiceNoteEditorRef}
                                            value={settings.invoice_notes || ""}
                                            config={editorConfig}
                                            onBlur={(newContent) => handleChange('invoice_notes', newContent)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Interest Note */}
                            <div className="col-12">
                                <div className="card radius-12 border border-warning">
                                    <div className="card-header py-12 px-16 bg-warning-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:percent" className="text-xl text-warning-600" />
                                        <h6 className="fw-semibold mb-0">Interest Note</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">This note will appear regarding interest charges.</p>
                                        <textarea
                                            className="form-control radius-8 bg-neutral-50"
                                            rows="3"
                                            value={settings.interest_notes || ""}
                                            onChange={(e) => handleChange('interest_notes', e.target.value)}
                                            placeholder="Enter interest related notes..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Debit Note Note */}
                            <div className="col-12">
                                <div className="card radius-12 border border-danger">
                                    <div className="card-header py-12 px-16 bg-danger-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:file-minus-outline" className="text-xl text-danger-600" />
                                        <h6 className="fw-semibold mb-0">Debit Note Note</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">This note will appear on debit notes.</p>
                                        <JoditEditor
                                            ref={debitNoteEditorRef}
                                            value={settings.debit_note_notes || ""}
                                            config={editorConfig}
                                            onBlur={(newContent) => handleChange('debit_note_notes', newContent)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Credit Note Note */}
                            <div className="col-12">
                                <div className="card radius-12 border border-info">
                                    <div className="card-header py-12 px-16 bg-info-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:file-plus-outline" className="text-xl text-info-600" />
                                        <h6 className="fw-semibold mb-0">Credit Note Note</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">This note will appear on credit notes.</p>
                                        <JoditEditor
                                            ref={creditNoteEditorRef}
                                            value={settings.credit_note_notes || ""}
                                            config={editorConfig}
                                            onBlur={(newContent) => handleChange('credit_note_notes', newContent)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Note Display Settings */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border border-info h-100">
                                    <div className="card-header py-12 px-16 bg-info-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:eye-settings-outline" className="text-xl text-info-600" />
                                        <h6 className="fw-semibold mb-0">Note Display Settings</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">Control visibility of notes on bills</p>
                                        <div
                                            className={`toggle-item d-flex align-items-center justify-content-between p-12 radius-8 mb-8 cursor-pointer ${settings.show_invoice_note === 1 ? 'bg-success-50' : 'bg-neutral-50'}`}
                                            onClick={() => handleToggle('show_invoice_note')}
                                        >
                                            <span className="text-sm fw-medium">Show Invoice Note</span>
                                            <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={settings.show_invoice_note === 1}
                                                    onChange={() => handleToggle('show_invoice_note')}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`toggle-item d-flex align-items-center justify-content-between p-12 radius-8 mb-8 cursor-pointer ${settings.show_receipt_note === 1 ? 'bg-success-50' : 'bg-neutral-50'}`}
                                            onClick={() => handleToggle('show_receipt_note')}
                                        >
                                            <span className="text-sm fw-medium">Show Receipt Note</span>
                                            <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={settings.show_receipt_note === 1}
                                                    onChange={() => handleToggle('show_receipt_note')}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`toggle-item d-flex align-items-center justify-content-between p-12 radius-8 mb-8 cursor-pointer ${settings.show_interest_note === 1 ? 'bg-success-50' : 'bg-neutral-50'}`}
                                            onClick={() => handleToggle('show_interest_note')}
                                        >
                                            <span className="text-sm fw-medium">Show Interest Note</span>
                                            <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={settings.show_interest_note === 1}
                                                    onChange={() => handleToggle('show_interest_note')}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`toggle-item d-flex align-items-center justify-content-between p-12 radius-8 mb-8 cursor-pointer ${settings.show_debit_note_note === 1 ? 'bg-success-50' : 'bg-neutral-50'}`}
                                            onClick={() => handleToggle('show_debit_note_note')}
                                        >
                                            <span className="text-sm fw-medium">Show Debit Note Note</span>
                                            <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={settings.show_debit_note_note === 1}
                                                    onChange={() => handleToggle('show_debit_note_note')}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`toggle-item d-flex align-items-center justify-content-between p-12 radius-8 cursor-pointer ${settings.show_credit_note_note === 1 ? 'bg-success-50' : 'bg-neutral-50'}`}
                                            onClick={() => handleToggle('show_credit_note_note')}
                                        >
                                            <span className="text-sm fw-medium">Show Credit Note Note</span>
                                            <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={settings.show_credit_note_note === 1}
                                                    onChange={() => handleToggle('show_credit_note_note')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Member Info Display */}
                            <div className="col-lg-6">
                                <div className="card radius-12 border border-secondary h-100">
                                    <div className="card-header py-12 px-16 bg-neutral-50 border-bottom d-flex align-items-center gap-2">
                                        <Icon icon="mdi:account-cog-outline" className="text-xl text-secondary-light" />
                                        <h6 className="fw-semibold mb-0">Member Info Display</h6>
                                    </div>
                                    <div className="card-body p-16">
                                        <p className="text-sm text-secondary-light mb-12">Control member details visibility on bills</p>
                                        <div
                                            className={`toggle-item d-flex align-items-center justify-content-between p-12 radius-8 mb-8 cursor-pointer ${settings.show_phone === 1 ? 'bg-success-50' : 'bg-neutral-50'}`}
                                            onClick={() => handleToggle('show_phone')}
                                        >
                                            <span className="text-sm fw-medium">Show Phone Number</span>
                                            <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={settings.show_phone === 1}
                                                    onChange={() => handleToggle('show_phone')}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`toggle-item d-flex align-items-center justify-content-between p-12 radius-8 mb-8 cursor-pointer ${settings.show_email === 1 ? 'bg-success-50' : 'bg-neutral-50'}`}
                                            onClick={() => handleToggle('show_email')}
                                        >
                                            <span className="text-sm fw-medium">Show Email ID</span>
                                            <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={settings.show_email === 1}
                                                    onChange={() => handleToggle('show_email')}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`toggle-item d-flex align-items-center justify-content-between p-12 radius-8 cursor-pointer ${settings.show_pan_card_no === 1 ? 'bg-success-50' : 'bg-neutral-50'}`}
                                            onClick={() => handleToggle('show_pan_card_no')}
                                        >
                                            <span className="text-sm fw-medium">Show PAN Card No.</span>
                                            <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={settings.show_pan_card_no === 1}
                                                    onChange={() => handleToggle('show_pan_card_no')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* Custom Styles */}
            <style>{`
                /* ===== Billing Type Card & Dropdown Styles ===== */
                .billing-type-card {
                    background: linear-gradient(135deg, #ffffff 0%, #fefce8 100%);
                    border: 1px solid #fef08a !important;
                }

                .billing-type-card .icon-wrapper {
                    width: 52px;
                    height: 52px;
                    background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
                }

                .dropdown-wrapper {
                    position: relative;
                }

                .custom-dropdown {
                    height: 52px;
                    font-size: 15px;
                    font-weight: 500;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 0 48px 0 16px;
                    background-color: #ffffff;
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23f59e0b' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
                    background-position: right 16px center;
                    background-size: 20px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                }

                .custom-dropdown:hover {
                    border-color: #f59e0b;
                    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
                }

                .custom-dropdown:focus {
                    border-color: #f59e0b;
                    box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.15);
                    outline: none;
                }

                .custom-dropdown:disabled {
                    background-color: #f9fafb;
                    cursor: not-allowed;
                    opacity: 0.7;
                }

                .custom-dropdown option {
                    padding: 12px;
                    font-weight: 500;
                }

                .dropdown-loader {
                    position: absolute;
                    right: 48px;
                    top: 50%;
                    transform: translateY(-50%);
                }

                /* ===== Enhanced Toggle Switch Styles ===== */
                .form-check-input {
                    width: 44px !important;
                    height: 22px !important;
                    cursor: pointer;
                    background-color: #e5e7eb;
                    border: none;
                    border-radius: 22px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23ffffff'/%3e%3c/svg%3e");
                    background-position: left center;
                    background-size: contain;
                }

                .form-check-input:hover {
                    background-color: #d1d5db;
                }

                .form-check-input:checked {
                    background-color: #f59e0b;
                    border-color: #f59e0b;
                    background-position: right center;
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23ffffff'/%3e%3c/svg%3e");
                }

                .form-check-input:checked:hover {
                    background-color: #d97706;
                }

                .form-check-input:focus {
                    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.25);
                    outline: none;
                }

                .form-switch .form-check-input {
                    background-size: auto;
                }

                /* Custom switch variant for important settings */
                .custom-switch {
                    width: 52px !important;
                    height: 26px !important;
                    cursor: pointer;
                }

                .custom-switch:checked {
                    background-color: #f59e0b;
                    border-color: #f59e0b;
                }

                /* ===== Tab Navigation Styles ===== */
                .tab-btn {
                    position: relative;
                    color: #6b7280;
                    transition: all 0.3s ease;
                    font-size: 14px;
                }

                .tab-btn:hover {
                    color: #f59e0b;
                    background-color: #fef3c7;
                }

                .tab-btn.active {
                    color: #f59e0b;
                    font-weight: 600;
                }

                .tab-btn.active::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #f59e0b, #f97316);
                    border-radius: 3px 3px 0 0;
                }

                /* ===== Setting Item Styles ===== */
                .cursor-pointer {
                    cursor: pointer;
                }

                .toggle-item, .setting-item {
                    transition: all 0.2s ease;
                    border: 1px solid transparent;
                }

                .toggle-item:hover, .setting-item:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                    border-color: #e5e7eb;
                }

                .transition-all {
                    transition: all 0.2s ease;
                }

                /* ===== Gradient & Color Styles ===== */
                .bg-gradient-primary {
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                }

                /* ===== Form Control Styles ===== */
                .form-control-lg, .form-select-lg {
                    font-size: 15px;
                    padding: 12px 16px;
                    border-radius: 10px;
                    border: 2px solid #e5e7eb;
                    transition: all 0.2s ease;
                }

                .form-control-lg:focus, .form-select-lg:focus {
                    border-color: #f59e0b;
                    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15);
                }

                /* ===== Size Utilities ===== */
                .w-40-px { width: 40px; }
                .h-40-px { height: 40px; }
                .h-44-px { height: 44px; }
                .w-80-px { width: 80px; }
                .h-80-px { height: 80px; }

                /* ===== Card Styles ===== */
                .card {
                    transition: all 0.2s ease;
                }

                .card:hover {
                    transform: translateY(-2px);
                }

                .border-danger-200 {
                    border-color: #fecaca !important;
                }

                .bg-opacity-20 {
                    background-opacity: 0.2;
                }

                /* ===== Alert Styles ===== */
                .alert-warning {
                    background-color: #fef3c7;
                    border-color: #fcd34d;
                    color: #92400e;
                }

                /* ===== Interest Charge Type Radio Styles ===== */
                .form-select {
                    border-radius: 10px;
                    border: 2px solid #e5e7eb;
                    transition: all 0.2s ease;
                }

                .form-select:focus {
                    border-color: #f59e0b;
                    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15);
                }
            `}</style>
        </div>
    );
};

export default FunctionalSetting;
